import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Building2, UserRound, Store, Shuffle, MessagesSquare,
  CalendarDays, Handshake, BookOpen, BadgeCheck, Sparkles, Send, Target, TrendingUp,
  Check, Plus, CheckCircle2, CircleDot, Circle,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import StatusBadge from "../components/StatusBadge";
import Marquee from "../components/Marquee";
import Counter from "../components/Counter";
import { NetworkHero, EventsIllustration, CommunityIllustration, CrossSellDiagram } from "../components/Illustrations";
import {
  AUDIENCE, FEATURES, BENEFITS, MEMBERSHIP_PLANS, EVENT_TYPES,
  COMMUNITY_TOPICS, CROSS_SELL_EXAMPLE, HUB_STATS, LAUNCH_TIMELINE,
} from "../data/content";

const ICONS = { Building2, UserRound, Store, Shuffle, MessagesSquare, CalendarDays, Handshake, BookOpen, BadgeCheck, Sparkles, Send, Target, TrendingUp };
const ease = [0.22, 1, 0.36, 1];
const HERO_LINES = ["One place where", "businesses grow", "together."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 md:pt-36" data-testid="hero">
      <div className="grid-lines absolute inset-0 -z-10 opacity-60" />
      <motion.div style={{ y: yBlob }} className="brand-bar absolute -right-40 -top-24 -z-10 h-[520px] w-[520px] rounded-full opacity-[0.12] blur-3xl" aria-hidden="true" />
      <div className="container-hub pb-16 lg:pb-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="mb-6">
              <span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> The StUpEco business growth network</span>
            </motion.div>
            <h1 className="font-display text-[2.4rem] font-semibold leading-[1.03] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl md:text-6xl lg:text-[4.6rem] lg:leading-[0.98]">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.05em]">
                  <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease, delay: 0.15 + i * 0.12 }}>
                    {i === 1 ? <>businesses <span className="brand-text">grow</span></> : line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.55 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">
              Discover businesses, professionals, opportunities, partnerships, events and resources — all inside one connected business network designed to help entrepreneurs grow.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.7 }} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/join" data-testid="hero-join-hub" className="group inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                Join Hub <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/features" data-testid="hero-explore-features" className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-6 py-3.5 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--ink)]">
                Explore Features <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-6 text-sm text-[color:var(--ink-soft)]">
              Be among the founding members of StUpEco Hub.
            </motion.p>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: yImg }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease, delay: 0.35 }} className="relative">
              <div className="hub-card relative overflow-hidden rounded-[28px] bg-[color:var(--soft)] p-8 lg:p-10">
                <NetworkHero className="w-full" />
              </div>
              <div className="hub-card absolute -bottom-5 -left-5 hidden items-center gap-3 rounded-2xl px-4 py-3 sm:flex">
                <span className="brand-bar flex h-9 w-9 items-center justify-center rounded-xl text-white"><Handshake className="h-4 w-4" /></span>
                <div>
                  <p className="text-xs font-semibold text-[color:var(--ink)]">Grow together</p>
                  <p className="text-[11px] text-[color:var(--ink-soft)]">Connect · Collaborate · Grow</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="border-y border-[color:var(--hairline)] bg-[color:var(--surface)] py-12" data-testid="section-stats">
      <div className="container-hub grid grid-cols-2 gap-8 md:grid-cols-4">
        {HUB_STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.06}>
            <div>
              <p className="font-display text-4xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-5xl">
                <Counter to={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-2 text-sm text-[color:var(--ink-soft)]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, sub, id, cta }) {
  return (
    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div className="max-w-3xl">
        {eyebrow && <Reveal><span className="eyebrow">{eyebrow}</span></Reveal>}
        <Reveal delay={0.05}>
          <h2 id={id} className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.05]">{title}</h2>
        </Reveal>
        {sub && <Reveal delay={0.1}><p className="mt-4 text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">{sub}</p></Reveal>}
      </div>
      {cta && <Reveal delay={0.1}>{cta}</Reveal>}
    </div>
  );
}

function WhatIs() {
  return (
    <section className="py-16 lg:py-24" data-testid="section-what-is">
      <div className="container-hub">
        <SectionHeading eyebrow="What is StUpEco Hub?"
          title="Not a directory. Not a marketplace. A business growth network."
          sub="StUpEco Hub brings the entire business ecosystem into one professional platform — the place where businesses discover opportunities far beyond traditional networking." />
        <Stagger className="mt-10 flex flex-wrap gap-3" gap={0.05}>
          {AUDIENCE.map((a) => (
            <StaggerItem key={a}>
              <span className="inline-flex items-center rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-5 py-2.5 text-sm font-medium text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)]">{a}</span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function FeaturesGrid() {
  const shown = FEATURES.slice(0, 6);
  return (
    <section className="bg-soft py-16 lg:py-24" data-testid="section-features-grid">
      <div className="container-hub">
        <SectionHeading eyebrow="Everything in one Hub" title="One platform for the whole business journey."
          cta={<Link to="/features" data-testid="features-grid-see-all" className="group inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-[color:var(--ink)]">See all {FEATURES.length} capabilities<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></Link>} />
        <Stagger className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" gap={0.06}>
          {shown.map((f) => {
            const Icon = ICONS[f.icon];
            return (
              <StaggerItem key={f.key}>
                <div data-testid={`feature-card-${f.key}`} className="hub-card group h-full rounded-2xl p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.08)]">
                  <div className="flex items-start justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--hairline)] bg-[color:var(--soft)] text-[color:var(--ink)] transition-transform duration-300 group-hover:scale-110">{Icon && <Icon className="h-5 w-5" />}</span>
                    <StatusBadge status={f.status} />
                  </div>
                  <h3 className="font-display mt-5 text-lg font-semibold tracking-tight text-[color:var(--ink)]">{f.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--ink-soft)]">{f.short}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function WhyJoin() {
  return (
    <section className="py-16 lg:py-24" data-testid="section-why-join">
      <div className="container-hub">
        <SectionHeading eyebrow="Why join?" title="Reasons to grow inside the Hub." />
        <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 3) * 0.05}>
              <div className="group border-t border-[color:var(--hairline)] py-6">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-sm font-medium text-[color:var(--ink-soft)] transition-colors group-hover:text-[color:var(--brand-teal)]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight text-[color:var(--ink)]">{b.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--ink-soft)]">{b.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function MembershipPreview() {
  return (
    <section className="bg-soft py-16 lg:py-24" data-testid="section-membership-preview">
      <div className="container-hub">
        <SectionHeading eyebrow="Membership" title="Three ways to be part of the Hub."
          sub="Start free and grow into full participation and premium visibility. Pricing will be announced later." />
        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {MEMBERSHIP_PLANS.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.08}>
              <div data-testid={`membership-preview-${plan.name.toLowerCase()}`} className={`relative h-full rounded-2xl p-8 ${plan.highlight ? "bg-[color:var(--invert-bg)] text-white" : "hub-card"}`}>
                {plan.highlight && <span className="brand-bar absolute right-6 top-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">Most popular</span>}
                <p className={`font-display text-2xl font-semibold tracking-tight ${plan.highlight ? "text-white" : "text-[color:var(--ink)]"}`}>{plan.name}</p>
                <p className={`mt-1 text-sm ${plan.highlight ? "text-white/70" : "text-[color:var(--ink-soft)]"}`}>{plan.tagline}</p>
                <div className={`my-6 h-px w-full ${plan.highlight ? "bg-white/15" : "bg-[color:var(--hairline)]"}`} />
                <ul className="space-y-3">
                  {plan.includes.slice(0, 5).map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${plan.highlight ? "text-[color:var(--brand-green)]" : "text-[color:var(--brand-teal)]"}`} />
                      <span className={plan.highlight ? "text-white/85" : "text-[color:var(--ink-soft)]"}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <Link to="/membership" data-testid="membership-preview-cta" className="group inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-6 py-3.5 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--ink)]">
              View Membership <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const TIMELINE_ICON = { done: CheckCircle2, active: CircleDot, upcoming: Circle };
function LaunchTimeline() {
  return (
    <section className="py-16 lg:py-24" data-testid="section-timeline">
      <div className="container-hub">
        <SectionHeading eyebrow="Road to launch" title="What we're building, in order."
          sub="StUpEco Hub is rolling out in phases. Here's where we are and what's next." />
        <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {LAUNCH_TIMELINE.map((t, i) => {
            const Icon = TIMELINE_ICON[t.state];
            const color = t.state === "done" ? "var(--brand-green)" : t.state === "active" ? "var(--brand-orange)" : "var(--ink-soft)";
            return (
              <Reveal key={t.label} delay={i * 0.06}>
                <div className={`hub-card h-full rounded-2xl p-5 ${t.state === "active" ? "ring-1 ring-[color:var(--brand-orange)]/40" : ""}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color }}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <p className="mt-4 text-sm font-semibold text-[color:var(--ink)]">{t.label}</p>
                  <p className="mt-1 text-xs capitalize text-[color:var(--ink-soft)]">{t.state === "done" ? "Live" : t.state === "active" ? "In progress" : "Upcoming"}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Events() {
  return (
    <section className="bg-[color:var(--surface)] py-16 lg:py-24" data-testid="section-events">
      <div className="container-hub">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Professional events" title="Discover and host events that move business forward."
              sub="Members will be able to find relevant professional events — and publish their own — inside the Hub." />
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="hub-card overflow-hidden rounded-2xl bg-[color:var(--soft)] p-8"><EventsIllustration className="w-full" /></div>
            </Reveal>
            <Stagger className="mt-5 flex flex-wrap gap-2.5" gap={0.04}>
              {EVENT_TYPES.map((e) => (
                <StaggerItem key={e}><span className="inline-flex items-center rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-sm font-medium text-[color:var(--ink)]">{e}</span></StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}

function Community() {
  return (
    <section className="overflow-hidden py-16 lg:py-24" data-testid="section-community">
      <div className="container-hub">
        <SectionHeading eyebrow="Community" title="Real operating conversations — not social noise."
          sub="Members will participate in focused, business-only discussions across the topics that actually move companies forward." />
      </div>
      <div className="mt-12"><Marquee items={COMMUNITY_TOPICS} /></div>
    </section>
  );
}

function CrossSelling() {
  return (
    <section className="bg-[color:var(--invert-bg)] py-16 text-white lg:py-24" data-testid="section-cross-selling">
      <div className="container-hub">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="eyebrow text-white/60"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> Flagship feature · Cross-Selling</span>
            <h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.05]">Collaborate instead of compete.</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/70 sm:text-lg">Businesses inside the Hub can discover complementary businesses and work together — referring clients and winning larger engagements as a coalition.</p>
            <Stagger className="mt-8 flex flex-wrap gap-2.5" gap={0.06}>
              {CROSS_SELL_EXAMPLE.map((b, i) => (
                <StaggerItem key={b} className="flex items-center gap-2.5">
                  <span className="rounded-full border border-white/12 bg-white/[0.05] px-4 py-2 text-sm font-medium">{b}</span>
                  {i < CROSS_SELL_EXAMPLE.length - 1 && <Plus className="h-3.5 w-3.5 text-white/40" />}
                </StaggerItem>
              ))}
            </Stagger>
            <div className="mt-8 flex items-center gap-3 text-sm text-white/60"><span className="brand-bar h-px w-12" /> They collaborate and refer business to each other.</div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}><div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8"><CrossSellDiagram className="w-full" /></div></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-24 lg:py-32" data-testid="section-final-cta">
      <div className="grid-lines absolute inset-0 -z-10 opacity-50" />
      <div className="container-hub text-center">
        <Reveal><h2 className="font-display mx-auto max-w-3xl text-4xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-5xl lg:text-6xl lg:leading-[1.02]">Ready to grow with the <span className="brand-text">Hub</span>?</h2></Reveal>
        <Reveal delay={0.08}><p className="mx-auto mt-6 max-w-xl text-base text-[color:var(--ink-soft)] sm:text-lg">Join the early-access list today and be among the first businesses to grow inside StUpEco Hub.</p></Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/join" data-testid="final-cta-join" className="group inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">Join Hub <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
            <Link to="/features" data-testid="final-cta-learn-more" className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-7 py-4 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--ink)]">Learn More</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo title="One place where businesses grow together" description="StUpEco Hub is the business growth network connecting founders, startups, businesses, professionals, investors and growth partners to discover, connect, collaborate and grow together." path="/" />
      <Hero />
      <Stats />
      <WhatIs />
      <FeaturesGrid />
      <WhyJoin />
      <MembershipPreview />
      <LaunchTimeline />
      <Events />
      <Community />
      <CrossSelling />
      <FinalCTA />
    </>
  );
}
